'use client'

import { useRef, useEffect, useState, Suspense } from 'react'
import Pagination from './Pagination'
import CtaLarge from './ui/cta/CtaLarge'
import { GridCardFragment } from '@/lib/types/datocms/graphql/generated'
import { GridCard } from './GridCard'

const ITEMS_PER_PAGE = 6

type Props = {
	items: GridCardFragment[]
}

export default function PaginatedGridCards({ items }: Props) {
	const [currentPage, setCurrentPage] = useState(1) // Pagination (Desktop)
	const [visibleItems, setVisibleItems] = useState(ITEMS_PER_PAGE) // Load More (Mobile)
	const [isMobile, setIsMobile] = useState(false)
	const cardsRef = useRef<HTMLDivElement>(null)
	const totalPages = Math.ceil(items.length / ITEMS_PER_PAGE)

	useEffect(() => {
		const handleResize = () => {
			setIsMobile(window.innerWidth <= 768) // Set to true for mobile devices
		}
		handleResize()
		window.addEventListener('resize', handleResize)
		return () => window.removeEventListener('resize', handleResize)
	}, [])

	// Get the locations for the current visible range
	const currentItems = isMobile
		? items.slice(0, visibleItems) // Mobile logic
		: items.slice(
				(currentPage - 1) * ITEMS_PER_PAGE, // Start index
				currentPage * ITEMS_PER_PAGE // End index
			)

	const handleLoadMore = () => {
		setVisibleItems((prev) => prev + ITEMS_PER_PAGE)
	}

	const handlePageChange = (newPage: number) => {
		setCurrentPage(newPage)

		setTimeout(() => {
			if (!isMobile && cardsRef.current) {
				const navHeight = 80
				const elementPosition = cardsRef.current.getBoundingClientRect().top
				const offsetPosition = elementPosition + window.scrollY - navHeight

				window.scrollTo({
					top: offsetPosition,
					behavior: 'smooth',
				})
			}
		}, 0)
	}

	return (
		<div className="space-y-16">
			<div ref={cardsRef}>
				<div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 xl:gap-x-12 xl:gap-y-10">
					{currentItems.map((item) => (
						<GridCard key={item.id} {...item} />
					))}
				</div>
			</div>

			{/* Pagination for desktop */}
			{!isMobile && totalPages > 1 && (
				<Suspense fallback={null}>
					<Pagination
						currentPage={currentPage}
						totalPages={totalPages}
						onPageChange={handlePageChange}
						maxVisible={5}
						className="max-sm:hidden"
					/>
				</Suspense>
			)}

			{/* Load More for mobile */}
			{isMobile && visibleItems < items?.length && (
				<CtaLarge
					cta={{
						label: 'Load More',
						__typename: 'NavigationItemRecord',
						id: 'load-more',
						useExternalLink: false,
						openInNewWindow: false,
					}}
					variant="tertiary"
					className="mx-auto sm:hidden h-16 w-44 [&_span]:text-blue-dark text-xs"
					onClick={handleLoadMore}
				/>
			)}
		</div>
	)
}
